import classNames from "classnames";
import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import ModalComponent from "..";
import {
  useCreateDiscountMutation,
  useEditDiscountMutation,
} from "../../../redux/features/event/event.api";
import {
  CreatedDiscount,
  CreateDiscountPayload,
} from "../../../redux/features/event/event.types";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import {
  currencyFull,
  currencyOptions,
  currencyShorts,
} from "../../../utils/constants";
import {
  formatNumber,
  getCurrencySymbol,
  parseStringToNumber,
} from "../../../utils/helpers";
import { ModalCompProps } from "../../../utils/types";
import { DiscountSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import ComboBox from "../../dropdown/comboBox";
import Dropdown from "../../dropdown/dropdown";
import { CaretDown } from "../../icon/downcaret";
import { TextInput } from "../../inputs/textInput";
import { TabNav } from "../../tabNav";

export const DiscountModal = ({
  open,
  handleClose,
  mode = "create",
  discount,
  onSuccess = () => {},
}: ModalCompProps & {
  mode?: "create" | "edit";
  discount?: CreatedDiscount;
  onSuccess?: (d?: CreatedDiscount) => void;
}) => {
  const [createDiscount, { isLoading: creatingDiscount }] =
    useCreateDiscountMutation();

  const [editDiscount, { isLoading: editingDiscount }] =
    useEditDiscountMutation();

  const {
    handleSubmit,
    values,
    touched,
    handleBlur,
    handleChange,
    errors,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: discount?.name ?? "",
      type: discount?.type ?? "free",
      value: discount?.value ?? "100",
      currency: "NGN",
      quantityType: discount?.quantityType ?? "limited",
      quantity: discount?.quantity ?? "0",
    },
    enableReinitialize: true,
    validationSchema: DiscountSchema,
    onSubmit: async (vals) => {
      const payload: CreateDiscountPayload = {
        name: vals.name.replaceAll(" ", ""),
        type: vals.type === "amount" ? "amount" : "percentage",
        value: parseStringToNumber(vals.value)!,
        quantityType: vals.quantityType,
        quantity:
          vals.quantityType === "limited"
            ? parseStringToNumber(vals.quantity)!
            : undefined,
        currency:
          values.type === "amount"
            ? (values.currency as PayoutCurrency)
            : undefined,
      };

      if (mode === "edit") {
        const resp = await editDiscount({
          id: discount?._id!,
          data: {
            ...payload,
            name: vals.name === discount?.name! ? undefined : vals.name,
          },
        });

        if (resp.data?.status === "success") {
          onSuccess();
          resetForm();
        }
      }

      if (mode === "create") {
        const resp = await createDiscount(payload);

        if (resp.data?.status === "success") {
          onSuccess(resp.data.data);
          resetForm();
        }
      }
    },
  });

  const close = () => {
    resetForm();
    handleClose();
  };

  return (
    <ModalComponent
      open={open}
      handleClose={close}
      className="!p-"
      dialogClassName=""
      closeOnOverlayClick
      showHeader
      title={discount ? "Edit Discount" : "Create Discount"}
      subtitle={"Discounts can be applied to all events"}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form className="grid grid-cols-2 items-start gap-4">
        <TextInput
          type="text"
          name="name"
          value={values.name}
          placeholder="Enter discount name"
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (
              e.key.length === 1 && // skip control keys like Shift, Arrow, etc.
              !/^[a-zA-Z0-9]$/.test(e.key)
            ) {
              e.preventDefault(); // block character
            }
          }}
          hasTitle
          title="Dicount Name"
          errors={errors.name && touched.name ? errors.name : ""}
          containerClassName="col-span-full"
          inputClass=""
          pattern="[A-Za-z0-9]*"
        />

        <TabNav
          navs={[
            {
              title: "Free",
              to: "free",
              onClick: (selected) => {
                setFieldValue("value", "100");
                setFieldValue("type", selected);
              },
            },
            {
              title: "Percentage",
              to: "percentage",
              onClick: (selected) => {
                setFieldValue("value", "");
                setFieldValue("type", selected);
              },
            },
            {
              title: "Amount",
              to: "amount",
              onClick: (selected) => {
                setFieldValue("value", "");
                setFieldValue("type", selected);
              },
            },
          ]}
          activeTab={values.type}
          variant="switcher"
          tabClassName=""
          className="col-span-full"
        />

        {values.type === "amount" ? (
          <ComboBox
            menuItems={currencyOptions}
            onSelectOption={(selected) => {
              setFieldValue("currency", selected);
            }}
            menuClassName="!max-h-[200px] translate-x-[-12px] !z-[999]"
            position="bottom start"
            immediate
            disabled={mode === "edit"}
            type="text"
            name="currency"
            value={
              values.currency
                ? currencyFull[values.currency as PayoutCurrency]
                : undefined
            }
            placeholder={"Select currency"}
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title={"Currency"}
            errors={errors.currency && touched.currency ? errors.currency : ""}
            readOnly
            containerClassName="col-span-full"
            customIcon={<CaretDown />}
            customIconPosition="right"
          />
        ) : undefined}

        <NumericFormat
          suffix={values.type === "amount" ? undefined : "%"}
          prefix={
            values.type === "amount"
              ? getCurrencySymbol(
                  currencyShorts[values.currency as PayoutCurrency],
                )
              : undefined
          }
          decimalScale={2}
          thousandSeparator
          allowLeadingZeros={false}
          customInput={TextInput}
          type="text"
          name="value"
          value={values.value}
          placeholder={
            values.type === "amount" ? "Enter amount off" : "Enter amount off"
          }
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title={values.type === "amount" ? "Amount Off" : "Percent Off"}
          readOnly={values.type === "free"}
          errors={errors.value && touched.value ? errors.value : ""}
          containerClassName={classNames("", {
            "": values.type === "amount",
            "col-span-full": values.type !== "amount",
          })}
        />

        <Dropdown
          menuItems={[
            {
              name: "Unlimited",
              value: "Unlimited",
              onClick: () => {
                setFieldValue("quantity", Infinity);
                setFieldValue("quantityType", "unlimited");
              },
            },
            {
              name: "Limited",
              value: "Limited",
              onClick: () => {
                setFieldValue("quantity", "0");
                setFieldValue("quantityType", "limited");
              },
            },
          ]}
          position={"bottom start"}
          menuClassName="translate-x-[-12px] !z-[999]"
          menuItemsClassName=""
        >
          <TextInput
            type="text"
            name="quantityType"
            value={values.quantityType}
            placeholder="Select quantity type"
            onChange={handleChange}
            onBlur={handleBlur}
            hasTitle
            title="Quantity Type"
            readOnly
            customIcon={<CaretDown />}
            customIconPosition="right"
            errors={
              errors.quantityType && touched.quantityType
                ? errors.quantityType
                : ""
            }
            inputClass="capitalize"
          />
        </Dropdown>

        <TextInput
          type="text"
          name="quantity"
          value={
            values.quantityType === "unlimited"
              ? "∞"
              : formatNumber(values.quantity ?? "")
          }
          placeholder="Enter discount quantity"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Quantity"
          readOnly={values.quantityType === "unlimited"}
          errors={errors.quantity && touched.quantity ? errors.quantity : ""}
        />

        <div className="col-span-full grid w-full grid-cols-2 items-center justify-end gap-2 md:flex">
          <Button
            text="Cancel"
            type="button"
            onClick={() => {
              close();
            }}
            variation="plain-grey"
            className="md:max-w-fit"
          />
          <Button
            text={discount !== undefined ? "Save Changes" : "Create"}
            type="button"
            onClick={() => {
              handleSubmit();
            }}
            className="md:max-w-fit"
            loading={creatingDiscount || editingDiscount}
            disabled={creatingDiscount || editingDiscount}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
